import { useState, useEffect, useContext, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { has, isArray } from 'lodash'

import { toast } from 'react-toastify'

import { i18n } from '../../translate/i18n'
import api, { setIsLoggingOut } from '../../services/api'
import toastError from '../../errors/toastError'
import { SocketContext } from '../../context/Socket/SocketContext'
import { clearAllCachedSettings } from '../../helpers/settingsCache'
import moment from 'moment'
import { decodeToken } from 'react-jwt'
import {
  clearPushSubscription,
  ensureSubscribed
} from '../../helpers/pushSubscriptions'
import {
  getPushProvider,
  isFirebasePushProvider
} from '../../helpers/pushProvider'
import { getFirebasePushToken } from '../../helpers/firebasePush'

let isRefreshing = false
let refreshSubscribers = []

const subscribeTokenRefresh = callback => {
  refreshSubscribers.push(callback)
}

const onRefreshed = newToken => {
  refreshSubscribers.forEach(callback => callback(newToken))
  refreshSubscribers = []
}

const onRefreshFailed = () => {
  refreshSubscribers.forEach(callback => callback(null))
  refreshSubscribers = []
}

const useAuth = () => {
  const history = useHistory()
  const [isAuth, setIsAuth] = useState(false)
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState({})

  const socketManager = useContext(SocketContext)

  useEffect(() => {
    const responseInterceptor = api.interceptors.response.use(
      response => response,
      async error => {
        const { getIsLoggingOut } = require('../../utils/logoutManager')
        const errorCode = error?.response?.data?.error

        const originalRequest = error.config
        if (errorCode === 'ERR_OUT_OF_WORK_SCHEDULE') {
          localStorage.removeItem('token')
          localStorage.removeItem('companyId')
          localStorage.removeItem('userId')
          localStorage.setItem('outOfScheduleWarning', 'true')
          api.defaults.headers.Authorization = undefined
          setIsAuth(false)
          setUser({})
          history.replace('/login')
          return Promise.reject(error)
        }

        if (
          error?.response?.status === 401 &&
          !originalRequest?._retry &&
          originalRequest?.url !== '/auth/refresh_token' &&
          originalRequest?.url !== '/auth/login'
        ) {
          if (getIsLoggingOut()) {
            return Promise.reject(error)
          }

          originalRequest._retry = true

          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              subscribeTokenRefresh(newToken => {
                if (newToken) {
                  originalRequest.headers.Authorization = `Bearer ${newToken}`
                  resolve(api(originalRequest))
                } else {
                  reject(originalRequest)
                }
              })
            })
          }

          isRefreshing = true

          try {
            const { data } = await api.post('/auth/refresh_token')
            const newToken = data.token

            localStorage.setItem('token', JSON.stringify(newToken))
            api.defaults.headers.Authorization = `Bearer ${newToken}`
            onRefreshed(newToken)

            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return api(originalRequest)
          } catch (refreshError) {
            const refreshStatus = refreshError?.response?.status
            const refreshErrorCode = refreshError?.response?.data?.error

            const shouldLogout =
              refreshStatus === 401 ||
              refreshStatus === 403 ||
              refreshErrorCode === 'ERR_SESSION_EXPIRED'

            if (shouldLogout) {
              localStorage.removeItem('token')
              localStorage.removeItem('companyId')
              clearAllCachedSettings()
              api.defaults.headers.Authorization = undefined
              setIsAuth(false)
              setUser({})
              history.replace('/login')
            }

            onRefreshFailed()
            return Promise.reject(refreshError)
          } finally {
            isRefreshing = false
          }
        }

        if (error?.response?.status === 402) {
          if (window.location.pathname !== '/subscription') {
            history.replace('/subscription')
          }
        }
        return Promise.reject(error)
      }
    )
    return () => {
      api.interceptors.response.eject(responseInterceptor)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const token = localStorage.getItem('token')
      ; (async () => {
        if (token) {
          try {
            const { data } = await api.post('/auth/refresh_token')
            api.defaults.headers.Authorization = `Bearer ${data.token}`
            setIsAuth(true)
            setUser(data.user)
          } catch (err) {
            toastError(err)
          }
        }
        setLoading(false)
      })()
  }, [])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    if (!companyId || !user.id) {
      return () => { }
    }

    const socket = socketManager.GetSocket(companyId)

    const onCompanyUserUseAuth = data => {
      if (data.action === 'update' && data.user.id === user.id) {
        setUser(data.user)
      }
    }

    socket.on(`company-${companyId}-user`, onCompanyUserUseAuth)

    return () => {
      socket.disconnect()
    }
  }, [user.id, socketManager])

  const posLogin = (data, impersonated = false) => {
    const {
      user: { company },
      token
    } = data

    const { companyId } = decodeToken(token)

    if (has(company, 'settings') && isArray(company.settings)) {
      const setting = company.settings.find(s => s.key === 'campaignsEnabled')
      if (setting && setting.value === 'true') {
        localStorage.setItem('cshow', null) //regra pra exibir campanhas
      }
    }

    moment.locale('pt-br')
    const dueDate = data.user.company.dueDate
    // const hoje = moment(moment()).format('DD/MM/yyyy')
    const vencimento = moment(dueDate).format('DD/MM/yyyy')

    var diff = moment(dueDate).diff(moment(moment()).format())

    var dias = moment.duration(diff).asDays()

    clearAllCachedSettings()

    localStorage.setItem('token', JSON.stringify(token))
    localStorage.setItem('companyId', companyId)
    localStorage.setItem('userId', data.user.id)
    localStorage.setItem('companyDueDate', vencimento)
    localStorage.setItem('impersonated', impersonated)
    api.defaults.headers.Authorization = `Bearer ${data.token}`
    setUser(data.user)
    setIsAuth(true)
    if (dias < 0) {
      toast.warn(
        `Sua assinatura venceu há ${Math.round(dias) * -1} ${Math.round(dias) * -1 === 1 ? 'dia' : 'dias'} `
      )
    } else if (Math.round(dias) < 5) {
      toast.warn(
        `Sua assinatura vence em ${Math.round(dias)} ${Math.round(dias) === 1 ? 'dia' : 'dias'} `
      )
    }
    // else {
    //   toast.success(i18n.t("auth.toasts.success"));
    // }
    if (data.user.profile === 'admin' && !data.user.hideAdminUI) {
      history.push('/')
    } else {
      history.push('/tickets')
    }
  }

  const handleLogin = async userData => {
    setLoading(true)

    try {
      const loginPayload = { ...userData }
      loginPayload.email = loginPayload.email?.replace(/\s+/g, '') || ''
      loginPayload.password = loginPayload.password?.replace(/\s+/g, '') || ''
      const pushProvider = getPushProvider()

      const isPushPermissionDenied = err => {
        const message = err?.message || ''
        return (
          (typeof Notification !== 'undefined' &&
            Notification.permission === 'denied') ||
          /permission|denied|not granted/i.test(message)
        )
      }

      if (isFirebasePushProvider()) {
        try {
          loginPayload.firebaseToken = await getFirebasePushToken()
        } catch (err) {
          if (isPushPermissionDenied(err)) {
            toast.warn(i18n.t('auth.toasts.pushPermissionDenied'))
          } else {
            toastError(i18n.t('auth.toasts.pushError'))
          }
        }
      } else {
        const vapidPublicKey = await api.get('/public-settings/vapidPublicKey')
        await clearPushSubscription()
        try {
          loginPayload.pushSubscription = await ensureSubscribed(
            vapidPublicKey.data
          )
        } catch (err) {
          if (isPushPermissionDenied(err)) {
            toast.warn(i18n.t('auth.toasts.pushPermissionDenied'))
          } else {
            toastError(i18n.t('auth.toasts.pushError'))
          }
        }
      }

      loginPayload.pushProvider = pushProvider

      const { data } = await api.post('/auth/login', loginPayload)
      posLogin(data)
    } catch (err) {
      const errorCode = err?.response?.data?.error

      if (
        errorCode === 'ERR_LOGIN_ACCESS_DENIED' ||
        errorCode === 'ERR_INVALID_CREDENTIALS' ||
        errorCode === 'ERR_USER_INACTIVE' ||
        errorCode === 'ERR_OUT_OF_WORK_SCHEDULE'
      ) {
        throw err
      }

      toastError(err)
    } finally {
      setLoading(false)
    }
  }

  const handleAccessRecoveryLogin = async recoveryCode => {
    setLoading(true)

    try {
      const { data } = await api.post('/auth/access-recovery/complete', {
        code: recoveryCode
      })
      posLogin(data)
    } catch (err) {
      toastError(err)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const handleImpersonate = async companyId => {
    setLoading(true)

    try {
      await clearPushSubscription()
      const { data } = await api.get(`/auth/impersonate/${companyId}`)
      posLogin(data, true)
      setLoading(false)
      window.location.reload(false)
    } catch (err) {
      toastError(err)
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    setLoading(true)

    // Indicar que estamos em processo de logout
    setIsLoggingOut(true)

    let shouldCleanup = false

    try {
      const impersonatedFlag = localStorage.getItem('impersonated') === 'true'
      const token = localStorage.getItem('token')
      let impersonatedByToken = false

      if (token) {
        try {
          const decoded = decodeToken(JSON.parse(token))
          impersonatedByToken = Boolean(decoded?.impersonated)
        } catch (_) {
          impersonatedByToken = false
        }
      }

      if (impersonatedFlag || impersonatedByToken) {
        const { data } = await api.post('/auth/impersonate/back')
        localStorage.removeItem('impersonated')
        posLogin(data, false)
        setLoading(false)
        window.location.reload(false)
        return
      }

      shouldCleanup = true
      await clearPushSubscription()
      await api.delete('/auth/logout')
    } catch (err) {
      // Apenas mostrar erro se não for relacionado ao logout
      if (!err.message?.includes('401')) {
        toastError(err)
      }
    } finally {
      if (shouldCleanup) {
        socketManager.disconnectSocket()
        localStorage.clear()
        clearAllCachedSettings()
        setIsAuth(false)
        setUser({})
        api.defaults.headers.Authorization = undefined
        setLoading(false)
        history.push('/login')
      }

      // Desativar flag de logout após um pequeno delay
      setTimeout(() => setIsLoggingOut(false), 1000)
    }
  }

  const getCurrentUserInfo = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me')
      return data
    } catch (_) {
      return null
    }
  }, [])

  const loadUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me')
      setUser(data)
    } catch (err) {
      console.error('Erro ao carregar usuário:', err)
    }
  }, [])

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleAccessRecoveryLogin,
    handleImpersonate,
    handleLogout,
    getCurrentUserInfo,
    loadUser
  }
}

export default useAuth
